import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, ClipboardCheck, Flame, TrendingUp, Download, Search, Loader2, CalendarDays, Gauge, CalendarClock, Send,
} from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, getToken } from "../../lib/api";
import { colorFor, FOLLOWUP_STATUSES } from "../../lib/constants";
import { AdminShell } from "./AdminShell";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const PRIORITY_COLOR = { HOT: "#D64545", WARM: "#F2A900", NURTURE: "#123D73" };

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5 card-shadow">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500">{label}</span>
        <div className="grid place-items-center h-8 w-8 rounded-lg" style={{ background: `${tint}1a` }}>
          <Icon size={16} style={{ color: tint }} />
        </div>
      </div>
      <div className="mt-3 font-heading font-extrabold text-navy text-2xl">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [priority, setPriority] = useState("all");
  const [status, setStatus] = useState("all");
  const [tag, setTag] = useState("all");
  const [tagOptions, setTagOptions] = useState([]);

  const load = () => {
    const params = {};
    if (priority !== "all") params.priority = priority;
    if (status !== "all") params.status = status;
    if (tag !== "all") params.tag = tag;
    if (search.trim()) params.search = search.trim();
    api.get("/admin/leads", { params }).then((res) => setLeads(res.data.leads)).catch(() => {});
  };

  useEffect(() => {
    api.get("/admin/stats").then((res) => setStats(res.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/admin/tags").then((res) => setTagOptions(res.data.tags || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [priority, status, tag]);

  const sendDigest = () => {
    toast.promise(api.post("/admin/digest/send"), {
      loading: "Sending weekly digest…",
      success: (res) => `Digest sent to ${res.data.sent_to || "advisor"} (${res.data.new_leads} new, ${res.data.hot} hot).`,
      error: "Could not send digest (set an advisor email in Settings).",
    });
  };

  const exportCsv = () => {
    const url = `${API_BASE}/admin/export`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = "leads-export.csv";
        a.click();
      })
      .catch(() => toast.error("Export failed."));
  };

  if (loading) return <AdminShell title="Dashboard"><div className="grid place-items-center py-20 text-navy"><Loader2 className="animate-spin" /></div></AdminShell>;

  return (
    <AdminShell title="Dashboard" actions={
      <div className="flex items-center gap-2">
        <button data-testid="digest-btn" onClick={sendDigest} className="rounded-full border border-slate-200 text-navy text-sm font-heading font-semibold px-4 py-2 flex items-center gap-2 hover:bg-slate-50 transition-colors duration-200">
          <Send size={15} /> Send Weekly Digest
        </button>
        <button data-testid="export-btn" onClick={exportCsv} className="rounded-full bg-navy text-white text-sm font-heading font-semibold px-4 py-2 flex items-center gap-2 hover:bg-royal transition-colors duration-300">
          <Download size={16} /> Export CSV
        </button>
      </div>
    }>
      {/* stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={ClipboardCheck} label="Total Assessments" value={stats.total_assessments} tint="#123D73" />
        <StatCard icon={Users} label="Total Leads" value={stats.total_leads} tint="#061B3A" />
        <StatCard icon={CalendarDays} label="Today's Leads" value={stats.todays_leads} tint="#E9A21A" />
        <StatCard icon={CalendarDays} label="This Month" value={stats.this_month_leads} tint="#2E9B4B" />
        <StatCard icon={Flame} label="Hot Leads" value={stats.hot_leads} tint="#D64545" />
        <StatCard icon={TrendingUp} label="Warm Leads" value={stats.warm_leads} tint="#F2A900" />
        <StatCard icon={Users} label="Nurture Leads" value={stats.nurture_leads} tint="#123D73" />
        <StatCard icon={CalendarClock} label="Meeting Requests" value={stats.meeting_requests ?? 0} tint="#E9A21A" />
        <StatCard icon={Gauge} label="Avg. Health Score" value={`${stats.avg_score}/60`} tint="#2E9B4B" />
      </div>

      {/* filters */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="lead-search" value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search name, email, mobile…" className="pl-9 rounded-lg bg-white" />
        </div>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger data-testid="filter-priority" className="w-40 rounded-lg bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="HOT">Hot</SelectItem>
            <SelectItem value="WARM">Warm</SelectItem>
            <SelectItem value="NURTURE">Nurture</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="filter-status" className="w-44 rounded-lg bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {FOLLOWUP_STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={tag} onValueChange={setTag}>
          <SelectTrigger data-testid="filter-tag" className="w-44 rounded-lg bg-white"><SelectValue placeholder="All Tags" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tags / Sources</SelectItem>
            {tagOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* table */}
      <div className="mt-5 rounded-2xl bg-white border border-slate-200 overflow-hidden card-shadow">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr className="text-left">
                {["Name", "Contact", "Score", "Status", "Top Priority", "Priority", "Follow-up", "Date"].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">No leads yet.</td></tr>
              )}
              {leads.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => navigate(`/admin/leads/${l.id}`)}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors duration-150">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-navy">{l.full_name}</div>
                    <div className="text-xs text-slate-400">{l.city || "—"}</div>
                    {(l.tags || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {l.tags.slice(0, 3).map((t) => (
                          <span key={t} className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-royal/8 text-royal">{t}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    <div>{l.mobile}</div>
                    <div className="text-xs text-slate-400">{l.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-heading font-bold" style={{ color: colorFor(l.rating?.color) }}>{l.score}</span>
                    <span className="text-slate-400 text-xs">/60</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{l.rating?.label}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs max-w-[160px] truncate">{l.priorities?.[0]?.title || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide"
                      style={{ background: `${PRIORITY_COLOR[l.lead_priority]}1a`, color: PRIORITY_COLOR[l.lead_priority] }}>
                      {l.lead_priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{l.follow_up_status}</td>
                  <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">{(l.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminShell>
  );
}
